/**
 * src/lib/offline/outbox.js — durable queue of mutations made while offline.
 *
 * Every offline write (create/update/status/assignee/archive ...) is appended
 * here instead of being lost. The sync engine drains it FIFO when connectivity
 * returns. Backed by the `outbox` store in db.js (auto-increment local_id +
 * status / created_at / domain indexes).
 *
 * Op shape:
 *   { local_id, domain, type, entity_id, payload, created_at, status, retries, error }
 */

import { STORES, idbAdd, idbGet, idbPut, idbGetAll, idbDelete, idbIndexCount, idbIndexKeysOrdered } from './db';

/**
 * TICKET-578 — binary payload fields are stored as BYTES, never as a Blob/File.
 *
 * A `File` handed to us by a drop/`<input>` is a POINTER to a file on disk. WebKit
 * stores that pointer in IndexedDB rather than the bytes, so on the next drain the
 * handle can be dead: the upload then posts an empty body and Supabase Storage
 * answers `No content provided`, forever, until the op is dropped after 8 retries.
 * Re-writing the record (markFailed) also throws `Error preparing Blob/File data to
 * be stored in object store`. Both were measured on 2026-07-30 — the CEO's photos
 * sat at "waiting to sync" and then vanished.
 *
 * ArrayBuffer structured-clones by VALUE everywhere, so reading the bytes once at
 * enqueue time is what makes the queue durable. photoApplier rebuilds the Blob.
 */
const BINARY_FIELDS = ['blob', 'thumb_blob'];

async function toDurableBytes(payload) {
  const out = { ...payload };
  for (const key of BINARY_FIELDS) {
    const v = out[key];
    if (v && typeof v.arrayBuffer === 'function') {
      out[key] = { __bytes: await v.arrayBuffer(), type: v.type || '', name: v.name || undefined };
    }
  }
  return out;
}

/** Append a pending op. Returns the generated local_id. */
export async function enqueue(op) {
  const record = {
    domain: op.domain,
    type: op.type,
    entity_id: op.entity_id ?? null,
    payload: await toDurableBytes(op.payload ?? {}),
    created_at: op.created_at ?? Date.now(),
    status: 'pending',
    retries: 0,
    error: null,
  };
  return idbAdd(STORES.outbox, record);
}

/** All not-yet-synced ops (pending or failed), oldest first. */
export async function listPending() {
  const all = await idbGetAll(STORES.outbox);
  return (all || [])
    .filter((o) => o.status === 'pending' || o.status === 'failed')
    .sort((a, b) => (a.created_at - b.created_at) || (a.local_id - b.local_id));
}

/** Remove a synced op. */
export async function remove(localId) {
  return idbDelete(STORES.outbox, localId);
}

/** Mark an op failed (increments retries, records the error). */
export async function markFailed(localId, error) {
  const rec = await idbGet(STORES.outbox, localId);
  if (!rec) return undefined;
  rec.status = 'failed';
  rec.retries = (rec.retries || 0) + 1;
  rec.error = String(error || '');
  return idbPut(STORES.outbox, rec);
}

const DRAINABLE = ['pending', 'failed'];

/**
 * Count of not-yet-synced ops. KEYS-ONLY (TICKET-1105): this was
 * `listPending().length` — a getAll() that loaded every queued photo's bytes just
 * to count them, every 4s, for every user, and crashed WebKit's IDB/network
 * process on iOS. Never read record values on a polling path.
 */
export async function count() {
  return idbIndexCount(STORES.outbox, 'status', DRAINABLE);
}

/** Drain order (created_at, then local_id) of not-yet-synced ops — KEYS-ONLY. */
export async function listPendingIds() {
  return idbIndexKeysOrdered(STORES.outbox, 'created_at', 'status', DRAINABLE);
}

/** One op by local_id (the drain loads ops one at a time, never the whole store). */
export async function getOp(localId) {
  return idbGet(STORES.outbox, localId);
}

/** Empty the outbox (tests / hard reset). */
export async function clearOutbox() {
  const all = await idbGetAll(STORES.outbox);
  await Promise.all((all || []).map((o) => idbDelete(STORES.outbox, o.local_id)));
}
